<script lang="ts">
    
    import { fade } from 'svelte/transition';
    import type { Question } from "../Model/Question";
    import { createEventDispatcher, getContext } from "svelte";
    import QuestionForm from "./questionForm.svelte";
    import type { Readable } from "svelte/store";
    import { Badge, Icon } from "sveltestrap";
    import type { Section } from "../Model/Section";
    import AddQuestion from "./AddQuestion.svelte";
    import DeployIcon from "./DeployIcon.svelte";
    import { widthState } from '../Directives/widthState';    
    import Editable from './FormPreview/Editable.svelte';
    import SectionModal from './sectionModal.svelte';
    import Watch from './Watch.svelte';
    
    export let section: Section;

    const dispatch = createEventDispatcher();
    const readonly: Readable<boolean> = getContext('readonly');
    const expanAll: Readable<boolean> = getContext('expandAll');

    let production = false;
    let editSection = null;
    let questions: Readable<Question[]>;
    let containerClassName: string = '';
    let openBody = false;

    $: setupComponent(section);
    $: openBody = $expanAll;

    function setupComponent(section: Section): void {
        questions = section.questionsReadable;
        if (section.parentId) {
            containerClassName = 'border-end-0 rounded-start';
        } else {
            containerClassName = 'rounded';
        }
    }

    function downQuestion(question: Question): void {
        section.moveDown(question);
    }

    function upQuestion(question: Question): void {
        section.moveUp(question);
    }

    function onRemove() {
        if (!confirm(`¿Esta seguro de querer eliminar la sección "${section.name.value}"?`)) {
            return;
        }
        dispatch("remove", section);
    }

    function onEditClose(): void {
        section = section;
    }

    function onEdit(): void {
        editSection = section;
    }

    function onDown(): void {
        dispatch("downPosition", section);
    }

    function onUp(): void {
        dispatch("upPosition", section);
    }

    function toggleBody(): void {
        openBody = !openBody;
    }

</script>

<style lang="scss">

    .section {
        margin-top: 1rem;
        padding-left: 1rem;
        box-sizing: border-box;
    }

    .heading {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem;

        .controls {
            opacity: 0;
            transition: opacity 200ms;
        }

        &:hover {

            .controls {
                opacity: 1;
            }

        }
    }

    .name {
        font-size: 1.1rem;
        font-weight: bold;
    }

    .body {
        padding: 1rem;
    }

    .questions {
        margin: 0 -1rem;
        display: flex;
        flex-wrap: wrap;
    }

    .question {
        margin-top: 1rem;
    }

</style>

<div class="section" use:widthState={section.width}>
    <div class="border {containerClassName}">
        <div class="heading bg-light" class:border-bottom={openBody} on:click={toggleBody}>
            <div class="name">
                <DeployIcon isDeployed={openBody} />
                <Editable store={section.name} />
                <Watch store={section.required} let:value={required}>
                    {#if required}
                        <Icon name="shield-fill-exclamation" class="text-warning" />
                    {/if}
                </Watch>
            </div>
            {#if !production}
            <div class="controls btn-group btn-group-sm">
                <button
                    type="button"
                    class="btn btn-primary"
                    disabled={$readonly}
                    on:click|stopPropagation={onEdit}><Icon name="pencil" /></button>
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    disabled={$readonly}
                    on:click|stopPropagation={onDown}><Icon name="arrow-down" /></button>
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    disabled={$readonly}
                    on:click|stopPropagation={onUp}><Icon name="arrow-up" /></button>
                <button
                    type="button"
                    class="btn btn-danger"
                    disabled={$readonly}
                    on:click|stopPropagation={onRemove}><Icon name="x" /></button>
            </div>
            {/if}
        </div>
        {#if openBody}
        <div class="body" transition:fade={{ duration: 200 }}>
            <div class="d-flex justify-content-between align-items-center">
                <AddQuestion prepend {section} />
                <Badge color="primary">{$questions.length} preguntas</Badge>
            </div>
            <div class="questions">
                {#each $questions as question}
                    <QuestionForm
                        {question}
                        allowSubsections={!section.parentId}
                        on:downPosition={(event) => downQuestion(event.detail)}
                        on:upPosition={(event) => upQuestion(event.detail)} />
                {/each}
            </div>
            {#if $questions.length > 0}
            <div class="d-flex justify-content-between align-items-center mt-3">
                <AddQuestion {section} />
                <Badge color="primary">{$questions.length} preguntas</Badge>
            </div>
            {/if}
        </div>
        {/if}
    </div>
</div>

<SectionModal section={editSection} on:close={onEditClose} />
